import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { PaktLockup } from '../components/pakt'
import { COUNTRY_CONFIGS } from '../lib/country-config'
import CountrySelector from '../components/CountrySelector'
import type { AuthResponse } from '../types'

interface SelectedPlan {
  plano: string
  displayName: string
  priceBrlMonthly: number
  priceBrlYearly: number
  features: string[]
}

function validateCPF(raw: string): boolean {
  const d = raw.replace(/\D/g, '')
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false
  let s = 0
  for (let i = 0; i < 9; i++) s += +d[i] * (10 - i)
  let c1 = (s * 10) % 11; if (c1 >= 10) c1 = 0
  if (c1 !== +d[9]) return false
  s = 0
  for (let i = 0; i < 10; i++) s += +d[i] * (11 - i)
  let c2 = (s * 10) % 11; if (c2 >= 10) c2 = 0
  return c2 === +d[10]
}

function formatCPF(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`
}

function formatPhone(v: string): string {
  let p = v.replace(/[^\d+\s]/g, '')
  if (p && !p.startsWith('+')) p = '+' + p
  return p
}

function validatePhone(phone: string): boolean {
  if (!phone) return true
  return phone.replace(/\D/g, '').length >= 10
}

export default function Register() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Plano selecionado vindo da pricing page (?plan=starter | pro).
  // Antes era ignorado — cliente clicava "Starter R$ 79" e caía em
  // FREE silenciosamente. Agora persistimos pro Dashboard pós-cadastro
  // mostrar banner de ativação. Busca pricing pra exibir display + preço
  // atualizado (admin pode ter editado).
  const planParam = searchParams.get('plan')?.toUpperCase()
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan | null>(null)

  useEffect(() => {
    if (!planParam || planParam === 'FREE') return
    fetch('/api/pricing/plans')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const found = d?.plans?.find((p: any) => p.plano === planParam)
        if (found) {
          setSelectedPlan({
            plano: found.plano,
            displayName: found.displayName ?? found.plano,
            priceBrlMonthly: found.priceBrlMonthly ?? 0,
            priceBrlYearly: found.priceBrlYearly ?? 0,
            features: found.features ?? [],
          })
        }
      })
      .catch(() => null)
  }, [planParam])

  const [form, setForm] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmSenha: '',
    countryCode: 'BR',
    cpf: '',
    telefone: '',
  })
  const [error, setError] = useState('')
  const [cpfError, setCpfError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [loading, setLoading] = useState(false)

  function onChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target
    if (name === 'cpf') {
      const formatted = formatCPF(value)
      setForm(prev => ({ ...prev, cpf: formatted }))
      setCpfError(formatted.replace(/\D/g, '').length === 11 && !validateCPF(formatted) ? 'CPF inválido' : '')
    } else if (name === 'telefone') {
      const formatted = formatPhone(value)
      setForm(prev => ({ ...prev, telefone: formatted }))
      setPhoneError(formatted && !validatePhone(formatted) ? 'Telefone inválido (mínimo 10 dígitos)' : '')
    } else {
      setForm((prev) => ({ ...prev, [name]: value }))
    }
  }

  // Get nationalId config for selected country
  const nationalIdConfig = COUNTRY_CONFIGS[form.countryCode]?.nationalId

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (form.senha !== form.confirmSenha) {
      setError(t('register.passwordMismatch', 'Passwords do not match.'))
      return
    }

    if (form.senha.length < 8) {
      setError(t('register.passwordTooShort', 'Password must be at least 8 characters.'))
      return
    }

    if (form.countryCode === 'BR' && form.cpf && !validateCPF(form.cpf)) {
      setError('CPF inválido. Verifique o número informado.')
      return
    }

    if (form.telefone && !validatePhone(form.telefone)) {
      setError('Telefone inválido. Use o formato +55 77 991322975')
      return
    }

    setLoading(true)
    try {
      const res = await api.post<AuthResponse>('/api/auth/register', {
        nome: form.nome,
        email: form.email,
        senha: form.senha,
        countryCode: form.countryCode,
        cpf: form.cpf || undefined,
        telefone: form.telefone || undefined,
      })
      localStorage.setItem('pakt_token', res.token)
      if (res.user) {
        localStorage.setItem('pakt_user', JSON.stringify({
          nome: res.user.name,
          email: res.user.email,
          countryCode: res.user.countryCode || res.user.country || form.countryCode,
          cpf: res.user.cpf || form.cpf || null,
          telefone: res.user.telefone || form.telefone || null,
        }))
      }
      // Persiste plano escolhido pro Dashboard mostrar banner "ative seu
      // plano X". Usuário cadastrado sempre começa em FREE; admin ativa
      // manualmente até Stripe subscription estar pronto.
      if (selectedPlan) {
        localStorage.setItem('pakt_selected_plan', JSON.stringify(selectedPlan))
      }
      navigate('/dashboard')
    } catch (err: unknown) {
      let message = t('auth.registerError', 'Error creating account. Please try again.')
      if (err && typeof err === 'object' && 'body' in err) {
        const body = (err as { body: Record<string, unknown> }).body
        if (body?.message) {
          message = String(body.message)
        } else if (body?.error === 'Validation Error' && Array.isArray(body?.details)) {
          message = (body.details as Array<{ message: string }>).map(d => d.message).join('. ')
        } else if (body?.error) {
          message = String(body.error)
        }
      }
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/"><PaktLockup size={22} /></Link>
        </div>

        {/* Card do plano selecionado (vindo de ?plan=starter etc).
            Some quando não tem plan na URL ou é FREE (default). */}
        {selectedPlan && (
          <div className="mb-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                Plano selecionado
              </span>
              <Link to="/precos" className="text-xs text-indigo-600 hover:underline">
                trocar →
              </Link>
            </div>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-lg font-bold text-gray-900">{selectedPlan.displayName}</span>
              <span className="text-sm text-gray-600">
                R$ {selectedPlan.priceBrlMonthly.toLocaleString('pt-BR')}/mês
                {selectedPlan.priceBrlYearly > 0 && (
                  <span className="text-xs text-gray-500">
                    {' '}· R$ {selectedPlan.priceBrlYearly.toLocaleString('pt-BR')}/ano
                  </span>
                )}
              </span>
            </div>
            {selectedPlan.features.length > 0 && (
              <ul className="text-xs text-gray-700 space-y-0.5">
                {selectedPlan.features.slice(0, 3).map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-indigo-600 mt-0.5">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
                {selectedPlan.features.length > 3 && (
                  <li className="text-gray-500 italic pl-4">
                    + {selectedPlan.features.length - 3} outras
                  </li>
                )}
              </ul>
            )}
            <p className="mt-2 text-xs text-gray-600 italic">
              Cadastre-se primeiro (grátis). Após criar a conta, ativamos o {selectedPlan.displayName}.
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {t('register.title', 'Create your account')}
          </h1>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.name', 'Full Name')}
              </label>
              <input id="nome" name="nome" type="text" required value={form.nome} onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="Maria Silva" />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.email', 'Email')}
              </label>
              <input id="email" name="email" type="email" required value={form.email} onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="maria@example.com" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.country', 'Country')}
              </label>
              <CountrySelector
                value={form.countryCode}
                onChange={(code) => setForm(prev => ({ ...prev, countryCode: code, cpf: '' }))}
              />
            </div>

            {/* National ID — shown dynamically based on country */}
            {nationalIdConfig && (
              <div>
                <label htmlFor="cpf" className="block text-sm font-medium text-gray-700 mb-1">
                  {nationalIdConfig.fieldName}
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <input id="cpf" name="cpf" type="text" required value={form.cpf} onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition ${cpfError ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder={nationalIdConfig.fieldName === 'CPF' ? '000.000.000-00' : nationalIdConfig.fieldName} />
                {cpfError ? (
                  <p className="text-xs text-red-500 mt-1">{cpfError}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('register.nationalIdHint', 'Necessário para verificar sua identidade ao assinar e pagar')}
                  </p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.phone', 'Telefone')}
                <span className="text-red-400 ml-1">*</span>
              </label>
              <input id="telefone" name="telefone" type="tel" required value={form.telefone} onChange={onChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition ${phoneError ? 'border-red-400' : 'border-gray-300'}`}
                placeholder="+55 11 90000-0000" />
              {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
            </div>

            <div>
              <label htmlFor="senha" className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.password', 'Password')}
              </label>
              <input id="senha" name="senha" type="password" required value={form.senha} onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="••••••••" />
            </div>

            <div>
              <label htmlFor="confirmSenha" className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.confirmPassword', 'Confirm Password')}
              </label>
              <input id="confirmSenha" name="confirmSenha" type="password" required value={form.confirmSenha} onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="••••••••" />
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
              {loading ? t('register.loading', 'Creating account...') : t('register.submit', 'Create Account')}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            {t('register.hasAccount', 'Already have an account?')}{' '}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-medium">
              {t('register.loginLink', 'Sign in')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
